import { produce } from 'immer'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { useLogEvent } from '~/hooks/useLogEvent'
import { useSurveyQueryActiveBrandGroup } from '~/hooks/useSurveyQuery'
import { useTamRatio } from '~/hooks/useTamRatio'
import { type CellInfo, exists, NumFormat, type RowInfo } from '~/utils'

import {
  brand1IdAtom,
  brand2IdAtom,
  dataDisplayTypeAtom,
  groupAtom,
  groupInfosAtom,
  isPendingTabulationAtom,
} from '../atoms'
import {
  createFiveSegmentWithAllLabelMap,
  fiveSegmentsWithAll,
  type FiveSegmentWithAll,
  type OverlapColumnKey,
} from '../utils'

import { useOverlapData } from './useOverlapData'

export const useOverlapTableInfo = (): RowInfo<OverlapColumnKey>[] => {
  const { i18n, t } = useTranslation()
  const logEvent = useLogEvent()

  const brandGroup = useSurveyQueryActiveBrandGroup()

  const tamRatio = useTamRatio()

  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const brand1Id = useAtomValue(brand1IdAtom)
  const brand2Id = useAtomValue(brand2IdAtom)
  const [groupInfos, setGroupInfos] = useAtom(groupInfosAtom)
  const activeGroup = useAtomValue(groupAtom)
  const setIsPendingTabulation = useSetAtom(isPendingTabulationAtom)

  const overlapData = useOverlapData()

  const fiveSegmentWithAllLabelMapBase = React.useMemo(
    () => createFiveSegmentWithAllLabelMap(t),
    [t],
  )

  const fiveSegmentWithAllLabelMap = React.useMemo(
    () => ({
      ...fiveSegmentWithAllLabelMapBase,
      fiveSegment4: t('awarenoncustomers'),
    }),
    [fiveSegmentWithAllLabelMapBase, t],
  )

  const isEnglish = i18n.language === 'en-US'

  return React.useMemo(() => {
    const totalCount = NumFormat.format(
      overlapData.find((data) => data.brand1 === 'all' && data.brand2 === 'all')
        ?.count ?? NaN,
    )

    return [
      // ヘッダー1
      {
        brand1Name: {
          align: 'center',
          colSpan: 2,
          isColumnHeader: true,
          isHeader: true,
          rowSpan: 2,
          text: t('sample_size_totalcount', { totalCount }),
        },
        all: {
          align: 'center',
          colSpan: 6,
          isColumnHeader: true,
          isHeader: true,
          text:
            brandGroup?.brands.find((brand) => brand.id === brand2Id)?.name ??
            t('brand_2'),
        },
      },
      // ヘッダー2
      R.mapToObj(fiveSegmentsWithAll, (fiveSegment) => [
        fiveSegment,
        {
          align: 'center',
          isColumnHeader: true,
          isHeader: true,
          text: fiveSegmentWithAllLabelMap[fiveSegment],
        } satisfies CellInfo,
      ]),
      // ブランド1の FiveSegment
      ...fiveSegmentsWithAll.map<RowInfo<OverlapColumnKey>>(
        (brand1FiveSegment) => {
          const brand1Name =
            brand1FiveSegment === 'all'
              ? ({
                  align: 'center',
                  isHeader: true,
                  rowSpan: 6,
                  text:
                    brandGroup?.brands.find((brand) => brand.id === brand1Id)
                      ?.name ?? t('brand_1'),
                  ...(isEnglish && {
                    textWritingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                  }),
                } satisfies CellInfo)
              : undefined

          return {
            brand1Name,
            brand1Segments: {
              align: 'center',
              isHeader: true,
              text: fiveSegmentWithAllLabelMap[brand1FiveSegment],
            },
            ...R.mapToObj(fiveSegmentsWithAll, (brand2FiveSegment) => {
              const equalsTo = <
                T extends {
                  brand1: FiveSegmentWithAll
                  brand2: FiveSegmentWithAll
                },
              >(
                item: T,
              ) =>
                item.brand1 === brand1FiveSegment &&
                item.brand2 === brand2FiveSegment

              const groupInfo = groupInfos.find(equalsTo)
              const group = groupInfo?.group

              const countData = overlapData.find(equalsTo)
              const count = countData?.count ?? NaN
              const ratio = countData?.ratio ?? NaN

              const text = exists(countData)
                ? NumFormat.forCount(count, ratio, tamRatio, dataDisplayType)
                : '-'

              const onClick =
                exists(countData) &&
                !(brand1FiveSegment === 'all' && brand2FiveSegment === 'all')
                  ? () => {
                      if (activeGroup !== group) {
                        setGroupInfos(
                          produce((draft) => {
                            const groupInfo = draft.find(equalsTo)
                            if (exists(groupInfo)) {
                              groupInfo.group = activeGroup
                            } else {
                              draft.push({
                                brand1: brand1FiveSegment,
                                brand2: brand2FiveSegment,
                                group: activeGroup,
                              })
                            }
                          }),
                        )

                        logEvent('Select overlap segment')
                      } else {
                        setGroupInfos(R.filter(R.isNot(equalsTo)))
                      }
                      setIsPendingTabulation(true)
                    }
                  : undefined

              return [
                brand2FiveSegment,
                {
                  group,
                  isBorderShownOnHoverWhenClickable: true,
                  text,
                  onClick,
                } satisfies CellInfo,
              ]
            }),
          }
        },
      ),
    ]
  }, [
    activeGroup,
    brand1Id,
    brand2Id,
    brandGroup?.brands,
    dataDisplayType,
    fiveSegmentWithAllLabelMap,
    groupInfos,
    logEvent,
    overlapData,
    setGroupInfos,
    setIsPendingTabulation,
    tamRatio,
    t,
    isEnglish,
  ])
}
